import { isRecord, stringField } from '../json';
import { warn, describeError } from '../log';
/**
 * Fetching a widget's configuration.
 *
 * Ported from src/loader/text-widget-loader.js rather than moved. The Stencil
 * widget never fetches its own config: the loader script fetches it and
 * spreads the result onto the custom element as ~106 properties, so the
 * component just reads props.
 *
 * A host that is not loaded by that script — a UI generated by AI Studio —
 * has nobody to set those properties, so the core has to fetch config itself.
 * This is the one part of the core that is new code rather than an
 * extraction, which is also why it is the part most likely to drift from the
 * loader. Changes to the config contract have to land in both.
 *
 * Kept faithful to the loader: same endpoint, same headers, same 10s timeout,
 * same treatment of a 200 response carrying a non-200 `status` in its body.
 */

const CONFIG_TIMEOUT_MS = 10000;

export interface RawWidgetConfig {
  [key: string]: unknown;
}

export interface FetchConfigOptions {
  marketplaceUrl: string;
  widgetId: string;
  source: string;
  /** Injectable for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

/**
 * A 200 response can still carry a failure in its body — the loader checks
 * `data.status` separately from the HTTP status, so we do too.
 */
export class ConfigUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfigUnavailableError';
  }
}

export async function fetchWidgetConfig({
  marketplaceUrl,
  widgetId,
  source,
  fetchImpl,
}: FetchConfigOptions): Promise<RawWidgetConfig> {
  const doFetch = fetchImpl || fetch;

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeout = setTimeout(() => controller?.abort(), CONFIG_TIMEOUT_MS);

  const headers: Record<string, string> = { 'widget-id': widgetId };
  if (source) headers['source'] = source;

  try {
    const response = await doFetch(`${marketplaceUrl}/chat-widget/public/config/${widgetId}`, {
      method: 'GET',
      headers,
      ...(controller ? { signal: controller.signal } : {}),
    });

    if (!response.ok) {
      throw new ConfigUnavailableError(`Failed to load widget config: HTTP ${response.status}`);
    }

    const data: unknown = await response.json();
    if (!isRecord(data)) {
      throw new ConfigUnavailableError('Widget config response was not an object');
    }
    // The service answers 200 with an error status in the body on some paths.
    if (typeof data.status === 'number' && data.status !== 200) {
      throw new ConfigUnavailableError(stringField(data, 'message') || 'Failed to load widget config');
    }
    if (!isRecord(data.config)) {
      throw new ConfigUnavailableError('Widget config response had no config');
    }
    return data.config as RawWidgetConfig;
  } finally {
    clearTimeout(timeout);
  }
}

/** The loader converts the server's kebab-case keys to camelCase props. */
export function kebabToCamel(key: string): string {
  return key.replace(/-([a-z])/g, (_m, c) => c.toUpperCase());
}

/**
 * Normalise the wire payload into the shape hosts are promised.
 *
 * The server sends kebab-case keys, and several values are JSON *strings*
 * rather than objects — `theme`, `contact-form-options`, `a-two-p-compliance`.
 * The loader script camelCases the keys onto element properties and the widget
 * parses the strings where it needs them, spread across the component.
 *
 * A host cannot be expected to know any of that. It reads `state.config`.
 */

export interface ContactFormField {
  id: string;
  value: string;
  label: string;
  dataType: string;
  fieldKey: string;
  placeholder: string;
  required: boolean;
  disabled?: boolean;
}

const JSON_VALUED_KEYS = ['theme', 'contactFormOptions', 'aTwoPCompliance', 'businessOfficeHours'];

const parseMaybeJson = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    // Handed back as the raw string. A host reading config.theme or
    // contactForm.fields then gets text where it expects an object, which is
    // worth knowing before it renders nothing.
    warn('a config value that should be JSON could not be parsed', {
      reason: describeError(e),
      preview: value.slice(0, 40),
    });
    return value;
  }
};

/** camelCase every key, and parse the values that arrive as JSON strings. */
export function normaliseConfig(raw: RawWidgetConfig): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(raw)) {
    const name = kebabToCamel(key);
    out[name] = JSON_VALUED_KEYS.includes(name) ? parseMaybeJson(raw[key]) : raw[key];
  }
  return out;
}
